import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { DialogContent, Typography, useMediaQuery } from '@material-ui/core';

// UTILITY
import { getDefaultCard } from '@/utility/user';
import useActions from '@/hooks/useActions';

// STORE
import { StreamingRewardsFundsActions } from '@/store/actions';

// COMPONENTS
import Loader from '@/components/Loader';
import FundsForm from '../FundsForm';

// STYLES
import { DialogStyled, CloseIcon } from './styles';

function FundsDialog(props) {
  const { onClose, open, cardReqParams } = props;
  const [t] = useTranslation();
  const upSm = useMediaQuery(({ breakpoints }) => breakpoints.up('sm'));

  const [
    cards,
    virtualCard,
    cardsLoading,
    paymentLoading,
    paymentError,
  ] = useSelector(({ streamingRewardsFunds: { errors, loadings }, user }) => [
    user.cards,
    user.virtualCard,
    user.loadings.cards,
    loadings.payment,
    errors.payment,
  ]);

  const [payFunds, resetState] = useActions([
    StreamingRewardsFundsActions.payFunds.action,
    StreamingRewardsFundsActions.resetState.action,
  ]);

  const defaultCard = getDefaultCard(cards);
  const loading = cardsLoading || paymentLoading;
  const loaderText =
    paymentLoading &&
    t(`notification.waitWhile${defaultCard ? 'PaymentProcessing' : 'CardAuthorizing'}`);

  useEffect(() => {
    return resetState;
  }, []);

  function handleDialogClose(e, reason) {
    if (reason === 'backdropClick' && loading) return;

    onClose();
  }

  return (
    <DialogStyled fullScreen={!upSm} fullWidth={upSm} open={open} onClose={handleDialogClose}>
      <DialogContent className="dialog-content">
        <CloseIcon size="medium" aria-label="close" onClick={onClose}>
          <i className="icon-close" />
        </CloseIcon>
        <Typography variant="h5" className="m-b-20" align="center">
          {t('streamingRewardsFunds.title')}
        </Typography>
        <FundsForm
          variant="dialog"
          onSuccessCallback={onClose}
          defaultCard={defaultCard}
          payFunds={data => payFunds({ ...data, cardReqParams })}
          errorMessage={paymentError}
          hideMonthlyPayment={Boolean(virtualCard?.has_user_monthly_funds)}
        />
      </DialogContent>
      <Loader active={loading} text={loaderText} />
    </DialogStyled>
  );
}
FundsDialog.defaultProps = {
  cardReqParams: {},
};

FundsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  cardReqParams: PropTypes.object,
};

export default React.memo(FundsDialog);
